// src/hooks/useBackup.ts
import { useCallback } from 'react';
import { Bill, BackupData } from '../types';

export interface BackupFileResult {
	link: HTMLAnchorElement;
	url: string;
	filename: string;
}

export interface UseBackupReturn {
	createBackupFile: (bills: Bill[]) => Promise<BackupFileResult>;
	restoreFromFile: (file: File) => Promise<BackupData>;
}

export function useBackup(): UseBackupReturn {
	const createBackupFile = useCallback(
		async (bills: Bill[]): Promise<BackupFileResult> => {
			const now = new Date();

			// Format date as MM/DD/YYYY
			const date = now.toLocaleDateString('en-US', {
				month: '2-digit',
				day: '2-digit',
				year: 'numeric',
			});

			// Format time as HH:MM
			const time = now.toLocaleTimeString('en-US', {
				hour12: false,
				hour: '2-digit',
				minute: '2-digit',
			});

			// Calculate stats
			const totalBills = bills.length;
			const totalAmount = bills.reduce((sum, bill) => sum + bill.amount, 0);

			// Create backup data object
			const backupData: BackupData = {
				version: '1.0',
				date: date,
				time: time,
				bills: bills,
				stats: {
					totalBills,
					totalAmount,
				},
			};

			// Create the JSON file
			const jsonBlob = new Blob([JSON.stringify(backupData, null, 2)], {
				type: 'application/json',
			});
			const jsonUrl = URL.createObjectURL(jsonBlob);
			const jsonLink = document.createElement('a');
			jsonLink.href = jsonUrl;

			// Format filename
			const filename = `Bill_Tracker_Backup_${date.replace(
				/\//g,
				'-'
			)}_${time.replace(':', '-')}.json`;
			jsonLink.download = filename;

			return {
				link: jsonLink,
				url: jsonUrl,
				filename,
			};
		},
		[]
	);

	const restoreFromFile = useCallback((file: File): Promise<BackupData> => {
		return new Promise((resolve, reject) => {
			if (file.type !== 'application/json') {
				reject(new Error('Please select a JSON backup file'));
				return;
			}

			const reader = new FileReader();
			reader.onload = (event) => {
				try {
					const result = event.target?.result;
					if (typeof result !== 'string') {
						throw new Error('Failed to read file content');
					}

					const backupData = JSON.parse(result) as BackupData;

					// Validate backup data structure
					if (
						!backupData.version ||
						!backupData.bills ||
						!Array.isArray(backupData.bills)
					) {
						throw new Error('Invalid backup file format');
					}

					resolve(backupData);
				} catch (error) {
					console.error('Restore error:', error);
					reject(new Error('Invalid backup file or corrupted data'));
				}
			};

			reader.onerror = () => reject(new Error('Failed to read file'));
			reader.readAsText(file);
		});
	}, []);

	return {
		createBackupFile,
		restoreFromFile,
	};
}
